import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { registerUser } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

export default function Register() {
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || !form.name) { setError("All fields required"); return; }
    if (form.password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true);
    setError("");
    try {
      const res = await registerUser(form);
      // If coming from signup flow, go back there
      if (location.state?.returnTo === '/signup') {
        navigate('/signup', { state: { ...location.state?.signupData } });
      } else {
        navigate('/dashboard', { state: { user: res.data } });
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#0A0F1F] flex items-center justify-center px-6" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="text-2xl font-bold text-white" style={{ fontFamily: "'Clash Display', 'Work Sans', sans-serif" }}>
            SiteSmith
          </Link>
          <p className="text-slate-400 mt-2">Create your account</p>
        </div>

        <div className="border border-slate-700 rounded-sm p-8 bg-slate-800/30">
          <button
            data-testid="google-register-btn"
            onClick={handleGoogleRegister}
            className="w-full flex items-center justify-center gap-3 border border-slate-600 rounded-sm py-3 px-4 text-white font-medium hover:bg-slate-700 transition-colors mb-6"
          >
            <svg viewBox="0 0 24 24" width="20" height="20"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Sign up with Google
          </button>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-slate-700" />
            <span className="text-sm text-slate-500">or</span>
            <div className="flex-1 h-px bg-slate-700" />
          </div>

          <form onSubmit={handleRegister} className="space-y-4">
            {error && (
              <div className="bg-red-900/30 border border-red-700 text-red-400 px-4 py-3 rounded-sm text-sm" data-testid="register-error">
                {error}
              </div>
            )}
            <div>
              <Label className="text-slate-300 mb-2 block">Full Name</Label>
              <Input
                data-testid="register-name-input"
                value={form.name}
                onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Mike Johnson"
                className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-600 focus:ring-orange-600 rounded-sm"
              />
            </div>
            <div>
              <Label className="text-slate-300 mb-2 block">Email</Label>
              <Input
                data-testid="register-email-input"
                type="email"
                value={form.email}
                onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="mike@example.com"
                className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-600 focus:ring-orange-600 rounded-sm"
              />
            </div>
            <div>
              <Label className="text-slate-300 mb-2 block">Password</Label>
              <Input
                data-testid="register-password-input"
                type="password"
                value={form.password}
                onChange={e => setForm(prev => ({ ...prev, password: e.target.value }))}
                placeholder="6+ characters"
                className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-600 focus:ring-orange-600 rounded-sm"
              />
            </div>
            <Button
              data-testid="register-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white py-3 rounded-sm font-semibold"
            >
              {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
              Create Account
            </Button>
          </form>

          <p className="text-center text-slate-500 text-sm mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-orange-500 hover:text-orange-400" data-testid="register-login-link">
              Log in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
